import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, RefreshCw, Edit, Trash2, Shield, Users, CheckSquare, UserCog, Eye } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import RoleModal from '../../components/admin/RoleModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { ROLE_PERMISSIONS } from '../../utils/permissions';
import { roleService } from '../../services';
import type { Role as ApiRole } from '../../services';
import { useTenantStore } from '../../stores/tenantStore';
import { useToast } from '../../components/common/ToastContainer';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';

interface Role {
  id: string;
  name: string;
  description: string;
  userCount: number;
  permissions: string[];
  isSystem: boolean;
  roleType?: string;
}

const Roles = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuthStore();
  const { selectedTenantId } = useTenantStore();

  // Roles are read-only for Tenant Admins: they may view but not create/edit/delete,
  // regardless of the RBAC role they hold. Only SuperAdmin (global) and ChannelAdmin
  // (within their channel scope) may manage roles — the backend enforces the same
  // rule (routes/roles.ts assertRoleMutator), this just hides the controls.
  const canManageRoles =
    user?.type === USER_TYPES.SUPER_ADMIN || user?.type === USER_TYPES.CHANNEL_ADMIN;
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [page] = useState(1);
  const [pageSize] = useState(20);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | undefined>();
  const [deleteRole, setDeleteRole] = useState<Role | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Map API role to UI format
  const mapApiRoleToUi = (apiRole: ApiRole): Role => ({
    id: apiRole.id,
    name: (apiRole as any).name || apiRole.roleName,
    description: apiRole.remark || (apiRole as any).description || '-',
    userCount: (apiRole as any).userCount ?? 0,
    permissions: (apiRole as any).permissions || [], // From updated API
    isSystem: apiRole.status === 1,
    roleType: (apiRole as any).role_type || 'Custom', // From updated API
  });

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.list({ page, pageSize, tenantId: selectedTenantId || undefined });
      const uiRoles = response.items.map(mapApiRoleToUi);
      setRoles(uiRoles);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [page, pageSize, selectedTenantId]);

  const handleRefresh = () => {
    fetchRoles();
  };

  const handleSaveRole = async (roleData: any) => {
    try {
      // Map role_type to permissions for predefined types
      const payload: any = {
        name: roleData.roleName,
        role_type: roleData.roleType,
        description: roleData.description,
        default_role: roleData.defaultRole ? 1 : 0,
        // Create the role under the tenant currently selected in the topbar so it
        // lands where the tenant-filtered list can see it. The API honours this
        // only for SuperAdmins; a TenantAdmin is pinned to their own tenant.
        tenant_id: selectedTenantId || undefined,
      };

      // Auto-assign permissions based on role_type (unless Custom)
      if (roleData.roleType === 'Custom') {
        payload.permissions = roleData.permissions;
      } else {
        payload.permissions = ROLE_PERMISSIONS[roleData.roleType as keyof typeof ROLE_PERMISSIONS];
      }

      if (editingRole) {
        // Update existing role
        await roleService.update(roleData.id, payload);
      } else {
        // Create new role
        await roleService.create(payload);
      }

      // Refresh the list
      await fetchRoles();
      setIsAddModalOpen(false);
      setEditingRole(undefined);
    } catch (error) {
      console.error('Failed to save role:', error);
      toast.error('Save Failed', 'Failed to save role. Please try again.');
    }
  };

  const handleEditClick = (role: Role) => {
    if (role.isSystem) {
      toast.info('System Role', 'System roles cannot be edited');
      return;
    }
    setEditingRole(role);
  };

  const handleDeleteClick = (role: Role) => {
    if (role.isSystem) {
      toast.info('System Role', 'System roles cannot be deleted');
      return;
    }
    setDeleteRole(role);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRole) return;

    try {
      setIsDeleting(true);
      await roleService.delete(deleteRole.id);
      await fetchRoles();
      setDeleteRole(undefined);
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error('Delete Failed', 'Failed to delete role. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<Role>[] = [
    { key: 'name', label: 'Role Name', sortable: true, render: (r) => <button onClick={() => navigate(`/admin/roles/${r.id}`)} className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"><Shield className="w-4 h-4 text-primary-600" /><span className="font-medium">{r.name}</span>{r.isSystem && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">System</span>}</button> },
    {
      key: 'roleType',
      label: 'Type',
      sortable: true,
      render: (r) => {
        const typeColors: Record<string, string> = {
          Admin: 'bg-red-100 text-red-700',
          Editor: 'bg-blue-100 text-blue-700',
          Viewer: 'bg-green-100 text-green-700',
          Custom: 'bg-purple-100 text-purple-700',
        };
        const colorClass = typeColors[r.roleType || 'Custom'] || 'bg-gray-100 text-gray-700';
        return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}>{r.roleType || 'Custom'}</span>;
      }
    },
    { key: 'description', label: 'Description', sortable: true },
    { key: 'userCount', label: 'Users', sortable: true, render: (r) => <div className="flex items-center space-x-1"><Users className="w-3 h-3 text-gray-400" /><span>{r.userCount}</span></div> },
    { key: 'permissions', label: 'Permissions', render: (r) => <div className="flex items-center space-x-1"><CheckSquare className="w-3 h-3 text-gray-400" /><span className="text-sm">{r.permissions.length > 0 ? `${r.permissions.length} assigned` : 'None'}</span></div> },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => navigate(`/admin/roles/${r.id}`)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <Eye className="w-4 h-4 text-gray-600 hover:text-primary-600" />
          </button>
          {canManageRoles && (
          <button
            onClick={() => handleEditClick(r)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            disabled={r.isSystem}
            title={r.isSystem ? 'System roles cannot be edited' : 'Edit Role'}
          >
            <Edit className={`w-4 h-4 ${r.isSystem ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-primary-600'}`} />
          </button>
          )}
          {canManageRoles && (
          <button
            onClick={() => handleDeleteClick(r)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            disabled={r.isSystem}
            title={r.isSystem ? 'System roles cannot be deleted' : 'Delete Role'}
          >
            <Trash2 className={`w-4 h-4 ${r.isSystem ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:text-red-600'}`} />
          </button>
          )}
        </div>
      )
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-bold text-gray-900">Roles & Permissions</h1><p className="text-gray-600 mt-1">Configure role-based access control</p></div>
        <div className="flex space-x-3"><button className="btn-secondary inline-flex items-center space-x-2"><Download className="w-4 h-4" /><span>Export</span></button><button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>{canManageRoles && <button onClick={() => setIsAddModalOpen(true)} className="btn-primary inline-flex items-center space-x-2"><Plus className="w-4 h-4" /><span>Add Role</span></button>}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Shield} title="Total Roles" value={roles.length} color="primary" />
        <StatCard icon={CheckSquare} title="System Roles" value={roles.filter(r => r.isSystem).length} color="success" />
        <StatCard icon={UserCog} title="Custom Roles" value={roles.filter(r => !r.isSystem).length} color="info" />
        <StatCard icon={Users} title="Total Users" value={roles.reduce((sum, r) => sum + r.userCount, 0)} color="secondary" />
      </div>
      <div className="card"><DataTable columns={columns} data={roles} searchPlaceholder="Search roles..." loading={loading} /></div>

      {/* Add/Edit Role Modal */}
      <RoleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveRole}
        mode="add"
      />

      <RoleModal
        isOpen={!!editingRole}
        onClose={() => setEditingRole(undefined)}
        onSave={handleSaveRole}
        role={editingRole}
        mode="edit"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteRole}
        onClose={() => setDeleteRole(undefined)}
        onConfirm={handleDeleteConfirm}
        title="Delete Role"
        message={`Are you sure you want to delete "${deleteRole?.name}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Roles;

